import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level error boundary: catches render/runtime errors anywhere in the tree
 * and shows a recoverable fallback instead of a blank white screen.
 */
export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Unhandled UI error:', error, info);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            fontFamily: 'var(--font-sans), system-ui, sans-serif',
            background: 'var(--color-bg)',
            color: 'var(--color-fg)',
          }}
        >
          <div
            className="glass-card"
            style={{
              maxWidth: 440,
              width: '100%',
              textAlign: 'center',
              padding: '2.5rem 2rem',
              borderRadius: 'var(--radius-xl)',
              boxShadow: 'var(--shadow-e3)',
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                margin: '0 auto 1.25rem',
                borderRadius: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--color-accent-container)',
                color: 'var(--color-on-accent-container)',
              }}
            >
              <i className="fas fa-triangle-exclamation" style={{ fontSize: '1.4rem' }} />
            </div>
            <h2 style={{ margin: 0, fontWeight: 800, letterSpacing: '-0.02em', fontSize: '1.4rem' }}>
              Something went wrong
            </h2>
            <p style={{ color: 'var(--color-muted)', marginTop: 8, lineHeight: 1.5 }}>
              An unexpected error occurred. You can try reloading the page.
            </p>
            <button
              onClick={this.handleReload}
              style={{
                marginTop: 20,
                padding: '10px 28px',
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                background: 'var(--color-primary)',
                color: 'var(--color-on-primary)',
                fontWeight: 600,
                fontSize: '0.95rem',
                cursor: 'pointer',
                boxShadow: 'var(--shadow-e1)',
              }}
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
